import React, { lazy, Suspense } from 'react';
import Private from "./Private";
import ErrorBoundary from "../../components/ErrorFallback";
import ChunkErrorBoundary from "../../components/ChunkErrorBoundary";
import FilterWrapper from "../../components/FilterWrapper";
import { NavigationProvider } from "../../contexts/NavigationContext";
import { FILTER_SCOPES } from "../../contexts/FilterContext";
import AuditDetail from 'features/audits/components/auditDetail/AuditDetail';
import StoreAuditsPage from 'features/audits/components/auditReport/StoreAuditsPage';
import AuditListDetailPage from 'features/auditDashboard/AuditListDetailPage';
import { UsersList } from 'features/users';
import Scratchers from 'features/scratchers';
// import Games from 'pages/store/components/games';

// Lazy load pages for better performance
const Stores = lazy(() => import("../../pages/Stores"));
const Wrapper = lazy(() => import("../../components/dashboard"));
const AddStore = lazy(() => import("../../pages/AddStore"));
const Store = lazy(() => import("../../pages/StoreDetails"));
const MyAccount = lazy(() => import("../../pages/MyAccount"));
const StoreSettings = lazy(() => import("../../pages/StoreSettings"));
const CStoreDetails = lazy(() => import("../../features/cstore/components/CStoreDetailsPage"));
const Pricebook = lazy(() => import("../../features/pricebook"));
const PricebookItemsPage = lazy(() => import("../../features/pricebook/pages/PricebookItemsPage"));
const PricebookGasPricePage = lazy(() => import("../../features/pricebook/pages/PricebookGasPricePage"));
const PricebookPurchasesPage = lazy(() => import("../../features/pricebook/pages/PricebookPurchasesPage"));
const PricebookPriceGroupsPage = lazy(() => import("../../features/pricebook/pages/PricebookPriceGroupsPage"));
const PricebookPackModifierPage = lazy(() => import("../../features/pricebook/pages/PricebookPackModifierPage"));
const PricebookPromotionsPage = lazy(() => import("../../features/pricebook/pages/PricebookPromotionsPage"));
const Games = lazy(() => import("../../features/games"));
const PricebookSettingsPage = lazy(() => import("../../features/pricebook/settings"));
const PricebookSettingsLinkedCodePage = lazy(() => import("../../features/pricebook/settings/pages/PricebookSettingsLinkedCodePage"));
const PurchaseInvoiceReviewPage = lazy(() => import("../../features/pricebook/tabs/purchases/components/PurchaseInvoiceReviewPage"));
const GamePackDetail = lazy(() => import("../../features/scratchers/components/GameDetail/GamePackDetail"));
const CurrentInventoryStatusPage = lazy(() => import("../../features/scratchers/CurrentInventoryStatusPage"));
const WebAuditScanPage = lazy(() => import("../../features/audits/components/webAudit/WebAuditScanPage"));
const WebPackManagementPage = lazy(() => import("../../features/audits/components/webAudit/WebPackManagementPage"));

// Loading component
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px'
  }}>
    Loading...
  </div>
);

export const Pages = {
  element: (
    <NavigationProvider>
      <Private />
    </NavigationProvider>
  ),
  children: [
    {
      path: "/store/:id/audit-scan",
      element: (
        <ErrorBoundary>
          <ChunkErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <WebAuditScanPage />
            </Suspense>
          </ChunkErrorBoundary>
        </ErrorBoundary>
      ),
    },
    {
      path: "/store/:id/audit-scan/pack-management",
      element: (
        <ErrorBoundary>
          <ChunkErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <WebPackManagementPage />
            </Suspense>
          </ChunkErrorBoundary>
        </ErrorBoundary>
      ),
    },
    {
      path: "/",
      element: (
        <ErrorBoundary>
          <ChunkErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Wrapper />
            </Suspense>
          </ChunkErrorBoundary>
        </ErrorBoundary>
      ),
      children: [
        {
          path: "/stores",
          element: (
            <ErrorBoundary>
              <FilterWrapper scope={FILTER_SCOPES.stores}>
                <ChunkErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <Stores />
                  </Suspense>
                </ChunkErrorBoundary>
              </FilterWrapper>
            </ErrorBoundary>
          ),
        },
        {
          path: "/add_store",
          element: (
            <ErrorBoundary>
              <ChunkErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <AddStore />
                </Suspense>
              </ChunkErrorBoundary>
            </ErrorBoundary>
          ),
        },
        {
          path: "/store/:id",
          element: (
            <ErrorBoundary>
              <ChunkErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <Store />
                </Suspense>
              </ChunkErrorBoundary>
            </ErrorBoundary>
          ),
          children: [
            {
              index: true,
              element: (
                <ErrorBoundary>
                  <FilterWrapper scope={FILTER_SCOPES.storeAuditReport}>
                    <ChunkErrorBoundary>
                      <Suspense fallback={<PageLoader />}>
                        <AuditListDetailPage />
                      </Suspense>
                    </ChunkErrorBoundary>
                  </FilterWrapper>
                </ErrorBoundary>
              ),
            },
            {
              path: "classic",
              element: (
                <ErrorBoundary>
                  <FilterWrapper scope={FILTER_SCOPES.storeAuditReport}>
                    <ChunkErrorBoundary>
                      <Suspense fallback={<PageLoader />}>
                        <StoreAuditsPage />
                      </Suspense>
                    </ChunkErrorBoundary>
                  </FilterWrapper>
                </ErrorBoundary>
              ),
            },
            {
              path: "audit-dashboard",
              element: (
                <ErrorBoundary>
                  <FilterWrapper scope={FILTER_SCOPES.storeAuditReport}>
                    <ChunkErrorBoundary>
                      <Suspense fallback={<PageLoader />}>
                        <AuditListDetailPage />
                      </Suspense>
                    </ChunkErrorBoundary>
                  </FilterWrapper>
                </ErrorBoundary>
              ),
            },
            {
              path: "audits",
              element: (
                <ErrorBoundary>
                  <ChunkErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <AuditDetail />
                    </Suspense>
                  </ChunkErrorBoundary>
                </ErrorBoundary>
              ),
            },
            {
              path: "audits/:auditId",
              element: (
                <ErrorBoundary>
                  <ChunkErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <AuditDetail />
                    </Suspense>
                  </ChunkErrorBoundary>
                </ErrorBoundary>
              ),
            },
            {
              path: "scratchers",
              element: (
                <ErrorBoundary>
                  <ChunkErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <Scratchers />
                    </Suspense>
                  </ChunkErrorBoundary>
                </ErrorBoundary>
              ),
            },
            {
              path: "scratchers/current-inventory-status",
              element: (
                <ErrorBoundary>
                  <ChunkErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <CurrentInventoryStatusPage />
                    </Suspense>
                  </ChunkErrorBoundary>
                </ErrorBoundary>
              ),
            },
            {
              path: "scratcher/:game_id?/packlifecycle/:pack_id?",
              element: (
                <ErrorBoundary>
                  <ChunkErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <GamePackDetail />
                    </Suspense>
                  </ChunkErrorBoundary>
                </ErrorBoundary>
              ),
            },
            {
              path: "sales",
              element: (
                <ErrorBoundary>
                  <ChunkErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <CStoreDetails />
                    </Suspense>
                  </ChunkErrorBoundary>
                </ErrorBoundary>
              ),
            },
            {
              path: "games",
              element: (
                <ErrorBoundary>
                  <ChunkErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <Games />
                    </Suspense>
                  </ChunkErrorBoundary>
                </ErrorBoundary>
              ),
            },
            {
              path: "pricebook",
              element: (
                <ErrorBoundary>
                  <ChunkErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <Pricebook />
                    </Suspense>
                  </ChunkErrorBoundary>
                </ErrorBoundary>
              ),
              children: [
                {
                  path: "items",
                  element: (
                    <ErrorBoundary>
                      <ChunkErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <PricebookItemsPage />
                        </Suspense>
                      </ChunkErrorBoundary>
                    </ErrorBoundary>
                  ),
                },
                {
                  path: "gas-price",
                  element: (
                    <ErrorBoundary>
                      <ChunkErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <PricebookGasPricePage />
                        </Suspense>
                      </ChunkErrorBoundary>
                    </ErrorBoundary>
                  ),
                },
                {
                  path: "purchases",
                  element: (
                    <ErrorBoundary>
                      <ChunkErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <PricebookPurchasesPage />
                        </Suspense>
                      </ChunkErrorBoundary>
                    </ErrorBoundary>
                  ),
                },
                {
                  path: "price-groups",
                  element: (
                    <ErrorBoundary>
                      <ChunkErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <PricebookPriceGroupsPage />
                        </Suspense>
                      </ChunkErrorBoundary>
                    </ErrorBoundary>
                  ),
                },
                {
                  path: "pack-modifier",
                  element: (
                    <ErrorBoundary>
                      <ChunkErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <PricebookPackModifierPage />
                        </Suspense>
                      </ChunkErrorBoundary>
                    </ErrorBoundary>
                  ),
                },
                {
                  path: "promotions",
                  element: (
                    <ErrorBoundary>
                      <ChunkErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <PricebookPromotionsPage />
                        </Suspense>
                      </ChunkErrorBoundary>
                    </ErrorBoundary>
                  ),
                },
                {
                  path: "purchases/:purchase_id",
                  element: (
                    <ErrorBoundary>
                      <ChunkErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <PurchaseInvoiceReviewPage />
                        </Suspense>
                      </ChunkErrorBoundary>
                    </ErrorBoundary>
                  ),
                },
                {
                  path: "purchase/:purchase_id",
                  element: (
                    <ErrorBoundary>
                      <ChunkErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <PurchaseInvoiceReviewPage />
                        </Suspense>
                      </ChunkErrorBoundary>
                    </ErrorBoundary>
                  ),
                },
                {
                  path: "settings",
                  element: (
                    <ErrorBoundary>
                      <ChunkErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <PricebookSettingsPage />
                        </Suspense>
                      </ChunkErrorBoundary>
                    </ErrorBoundary>
                  ),
                  children: [
                    {
                      path: "vendors",
                      element: <></>,
                    },
                    {
                      path: "departments",
                      element: <></>,
                    },
                    {
                      path: "tax",
                      element: <></>,
                    },
                    {
                      path: "linked-code",
                      element: (
                        <ErrorBoundary>
                          <ChunkErrorBoundary>
                            <Suspense fallback={<PageLoader />}>
                              <PricebookSettingsLinkedCodePage />
                            </Suspense>
                          </ChunkErrorBoundary>
                        </ErrorBoundary>
                      ),
                    },
                    {
                      path: "age-restriction",
                      element: <></>,
                    },
                  ],
                },
              ],
            },
            {
              path: "users",
              element: (
                <ErrorBoundary>
                  <ChunkErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <UsersList />
                    </Suspense>
                  </ChunkErrorBoundary>
                </ErrorBoundary>
              ),
            },
            {
              path: "stores-settings",
              element: (
                <ErrorBoundary>
                  <ChunkErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <StoreSettings />
                    </Suspense>
                  </ChunkErrorBoundary>
                </ErrorBoundary>
              ),
            },
          ],
        },
        {
          path: "/my_account",
          element: (
            <ErrorBoundary>
              <ChunkErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <MyAccount />
                </Suspense>
              </ChunkErrorBoundary>
            </ErrorBoundary>
          ),
        },
      ],
    },
  ],
};
